import React from 'react'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
// Importa Box de Material-UI
import { Grid, Box } from '@mui/material'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { es } from 'date-fns/locale/es'
import { format, parse } from 'date-fns'

/**
 * @typedef {import('@mui/x-date-pickers/DatePicker').DatePickerProps<Date>} MuiDatePickerProps
 */

/**
 * @typedef {object} FilterDateChangeEvent
 * @property {object} target - Un objeto que simula la estructura de un evento de cambio nativo.
 * @property {string} target.name - El nombre del campo, proveniente de la prop `name`.
 * @property {string} target.value - La fecha seleccionada en formato 'dd/MM/yyyy', o una cadena vacía si se limpia el campo.
 */

/**
 * @typedef {object} FilterDateProps
 * @property {string} [label] - El texto de la etiqueta que se muestra a la izquierda del selector de fecha.
 * @property {string} [value] - El valor actual de la fecha en formato 'dd/MM/yyyy'.
 * @property {string} name - El nombre del campo, que se incluirá en el objeto de evento sintético del `onChange`.
 * @property {function(FilterDateChangeEvent): void} onChange - La función callback que se ejecuta cuando se selecciona una fecha.
 * @property {string} [defaultValue] - El valor por defecto de la fecha en formato 'dd/MM/yyyy', usado si `value` no está definido.
 * @property {string} [minDate] - La fecha mínima seleccionable en formato 'dd/MM/yyyy'.
 * @property {string} [maxDate] - La fecha máxima seleccionable en formato 'dd/MM/yyyy'.
 */

/**
 * Un componente de selector de fecha especializado para paneles de filtros.
 * 
 * Utiliza `DatePicker` de MUI X con localización en español. Acepta y devuelve fechas
 * en formato 'dd/MM/yyyy' como cadenas de texto, facilitando su integración con sistemas
 * de filtrado basados en URL o formularios. Deshabilita la selección de fechas futuras
 * por defecto (`disableFuture`).
 * 
 * @component
 * @example
 * const MyFilterPanel = () => {
 *   const [startDate, setStartDate] = useState('01/01/2025');
 * 
 *   const handleDateChange = (event) => {
 *     console.log(`Campo: ${event.target.name}, Valor: ${event.target.value}`);
 *     setStartDate(event.target.value);
 *   };
 * 
 *   return (
 *     <FilterDate
 *       label="Desde:"
 *       name="startDate"
 *       value={startDate}
 *       onChange={handleDateChange}
 *       maxDate="31/12/2025"
 *     />
 *   );
 * };
 * 
 * @param {FilterDateProps & MuiDatePickerProps} props - Las props del componente FilterDate.
 */
export const FilterDate = ({
  label,
  value,
  name,
  onChange,
  defaultValue,
  minDate,
  maxDate,
  ...props
}) => {
  const parseDate = (dateString) => {
    if (!dateString) return null
    const parsed = parse(dateString, 'dd/MM/yyyy', new Date())
    return isNaN(parsed) ? null : parsed
  }

  const objectDate = parseDate(value) || parseDate(defaultValue)
  const minDateValue = parseDate(minDate)
  const maxDateValue = parseDate(maxDate)

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={es}>
      <Grid container justifyContent="space-around" alignItems="center" sx={{ padding: '8px 0' }}>
        {label && <Box sx={{ marginRight: 1 }}>{label}</Box>}
        <DatePicker
          format="dd/MM/yyyy"
          name={name}
          value={objectDate}
          onChange={(date) => {
            if (date && date.toString() !== 'Invalid Date') {
              const formattedDate = format(date, 'dd/MM/yyyy')
              onChange({ target: { name, value: formattedDate } })
            } else {
              onChange({ target: { name, value: '' } })
            }
          }}
          disableFuture
          minDate={minDateValue}
          maxDate={maxDateValue}
          slotProps={{
            textField: {
              variant: 'standard',
              placeholder: 'dd/mm/aaaa',
            },
          }}
          sx={{ width: '180px' }}
          {...props}
        />
      </Grid>
    </LocalizationProvider>
  )
}

export default FilterDate
